<template>
    <Card :title="$t('labels.custom_fields')">
        <table class="table table-bordered">
            <thead>
                <tr>
                    <th width="50%">{{ $t('labels.custom_field') }}</th>
                    <th width="50%">{{ $t('labels.filter_item') }}</th>
                    <th width="60px"></th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>
                        <AppSelect
                            :model-value="customField?.uuid"
                            :selected-value="customField?.name"
                            :selected-values="Object.keys(orderCustomFields)"
                            @update:model-value="selectCustomField"
                            :options="visibleCustomFields.map(field => ({ key: field.uuid, value: field.name }))"
                            optionKey="key"
                            optionValue="value"
                        />
                    </td>
                    <td>
                        <AppSelect
                            v-if="customField.type === 'select'"
                            :modelValue="customFieldValue?.key"
                            :selectedValue="customFieldValue?.value"
                            @update:model-value="customFieldValue = $event"
                            optionKey="key"
                            optionValue="value"
                            :options="customField.select_items.map(item => ({ key: item, value: item }))"
                        />
                        <AppCheckbox
                            v-else-if="customField.type === 'checkbox'"
                            v-model="customFieldValue"
                        />
                        <textarea
                            v-else-if="customField.type === 'longtext'"
                            :value="customFieldValue"
                            class="d-block"
                            style="width: 100%"
                            @input="customFieldValue = $event.target.value"
                        />
                        <Input
                            v-else
                            v-model="customFieldValue"
                            :type="customField.type === 'number' ? 'number' : 'text'"
                        />
                    </td>
                    <td>
                        <AppButton
                            size="sm"
                            color="light"
                            class="btn-icon"
                            @click="addCustomFieldValue"
                            :disabled="!customField.uuid || !customFieldValue"
                        >
                            <i class="icon-diff-added"></i>
                        </AppButton>
                    </td>
                </tr>
                <tr v-for="key in sortedOrderCustomFieldKeys" :key="key">
                    <td>{{ customFields.find(field => field.uuid === key)?.name }}</td>
                    <td>
                        <AppSelect
                            v-if="customFields.find(field => field.uuid === key).type === 'select'"
                            :modelValue="orderCustomFields[key]?.key"
                            :selectedValue="orderCustomFields[key]?.value"
                            @update:model-value="orderCustomFields[key] = $event"
                            optionKey="key"
                            optionValue="value"
                            :options="customFields.find(field => field.uuid === key).select_items.map(item => ({ key: item, value: item }))"
                        />
                        <AppCheckbox
                            v-else-if="customFields.find(field => field.uuid === key).type === 'checkbox'"
                            v-model="orderCustomFields[key]"
                        />
                        <textarea
                            v-else-if="customFields.find(field => field.uuid === key).type === 'longtext'"
                            :value="orderCustomFields[key]"
                            class="d-block"
                            style="width: 100%"
                            @input="orderCustomFields[key] = $event.target.value"
                        />
                        <InputGroup v-else-if="customFields.find(field => field.uuid === key).type === 'link'">
                            <Input
                                v-model="orderCustomFields[key]"
                                type="text"
                            />
                            <template #append>
                                <a :href="orderCustomFields[key]" target="_blank" class="btn btn-md btn-light btn-icon">
                                    <i class="icon-link"></i>
                                </a>
                            </template>                        
                        </InputGroup>
                        <Input
                            v-else
                            v-model="orderCustomFields[key]"
                            :type="customFields.find(field => field.uuid === key).type === 'number' ? 'number' : 'text'"
                        />
                    </td>
                    <td>
                        <AppButton
                            size="sm"
                            color="light"
                            class="btn-icon"
                            @click="() => {
                                delete orderCustomFields[key];
                            }"
                        >
                            <i class="icon-diff-removed"></i>
                        </AppButton>
                    </td>
                </tr>
            </tbody>
        </table>
        <div class="card-footer d-flex justify-content-end" @click="saveOrderCustomFields">
            <AppButton color="indigo">
                {{ $t('buttons.save') }}
            </AppButton>
        </div>
    </Card>
</template>
<script>
import Card from "../../../../../components/new/CardComponent.vue";
import FormGroup from "../../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../../components/new/form/Input.vue";
import AppSelect from "../../../../../components/new/form/select/AppSelect.vue";
import AppCheckbox from "../../../../../components/new/form/checkbox/AppCheckbox.vue";
import AppButton from "../../../../../components/new/button/AppButton.vue";
import InputGroup from "../../../../../components/new/form/InputGroup.vue";

import { httpClient } from "../../../../../services/HttpClient";
import MessageNotificator from "../../../../../services/MessageNotificator";

export default {
    name: "OrderCustomFieldsPage",
    components: {
        Card, FormGroup, Input, AppSelect, AppCheckbox, AppButton, InputGroup
    },
    data() {
        return {
            customField: {},
            customFields: [],
            orderCustomFields: {},
            customFieldValue: null
        }
    },
    computed: {
        visibleCustomFields() {
            return this.customFields.filter(field => field.show_in_order);
        },
        sortedOrderCustomFieldKeys() {
            return this.visibleCustomFields
                .map(field => field.uuid)
                .filter(uuid => uuid in this.orderCustomFields);
        }
    },
    methods: {
        saveOrderCustomFields() {
            let requestBody = Object.entries(this.orderCustomFields).map(([key, value]) => {
                return {
                    uuid: key,
                    value: typeof value === 'object' ? value.value : value
                }
            });

            httpClient.put(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.$route.params.orderUuid}/custom-fields`, {
                data: requestBody
            }).then(() => {
                new MessageNotificator().success(this.$t('alerts.success'));
            })
        },
        async fetchCustomFields() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/custom-fields`, {
                limit: 100,
                orders: [{ column: 'priority_order', dir: 'asc' }]
            })
                .then(response => {
                    this.customFields = response.data.data;
                })
        },
        fetchOrderCustomFields() {
            httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.$route.params.orderUuid}/custom-fields`, { limit: 100 })
                .then(response => {
                    response.data.data.forEach((customField) => {
                        if (this.customFields.find(field => field.uuid === customField.custom_field_uuid).type === 'select') {
                            this.orderCustomFields[customField.custom_field_uuid] = {
                                key: customField.value,
                                value: customField.value
                            };
                        } else {
                            this.orderCustomFields[customField.custom_field_uuid] = customField.value;
                        }
                    })
                })
        },
        selectCustomField(customField) {
            this.customField = this.customFields.find(field => field.uuid === customField.key) || {};
            this.customFieldValue = null;
        },
        addCustomFieldValue() {
            this.orderCustomFields[this.customField.uuid] = this.customFieldValue;
            this.customFieldValue = null;
            this.customField = {};
        }
    },
    async mounted() {
        await this.fetchCustomFields();
        this.fetchOrderCustomFields();
    }
}
</script>
